import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  computed,
  signal,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { IdentityModel } from '@core/models/identity.model';
import { InviteModel } from '@core/models/invite.model';
import * as fromRoot from '@core/redux';
import { select, Store } from '@ngrx/store';
import { AvatarComponent } from '@ux/components/avatar/avatar.component';

@Component({
  selector: 'ux-popup-user',
  templateUrl: './popup-user.component.html',
  imports: [FormsModule, ReactiveFormsModule, CommonModule, AvatarComponent],
  standalone: true,
  styleUrls: ['./popup-user.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopupUserComponent {
  protected readonly projectId = input<string>('');
  private readonly store = inject<Store<fromRoot.State>>(Store);
  @Input() header = 'Исполнитель';
  @Input() multiple = false;
  @Output() selectItem = new EventEmitter<IdentityModel>();
  @Output() changeItems = new EventEmitter<string[]>();

  protected readonly searchControl = new FormControl('');
  protected readonly selectIndex = signal<number>(-1);

  private readonly value$ = signal<string | string[]>('');
  private readonly items$ = toSignal(this.store.pipe(select(fromRoot.getInviteActiveAllEntities)), {
    initialValue: [] as InviteModel[],
  });

  protected readonly currentUser = toSignal(this.store.pipe(select(fromRoot.getUser)), {
    initialValue: null as IdentityModel | null,
  });

  protected readonly filteredItems = computed(() => {
    const items = this.items$();
    const searchValue = this.searchControl.value?.toLowerCase() || '';
    const currentProjectId = this.projectId();

    return items
      .filter((item: InviteModel) => {
        const user = item.user;
        const matchesProject = !currentProjectId || currentProjectId === item.projectId;

        return (
          matchesProject &&
          (user.firstName?.toLowerCase().includes(searchValue) ||
            user.lastName?.toLowerCase().includes(searchValue) ||
            user.email?.toLowerCase().includes(searchValue))
        );
      })
      .reduce((unique: InviteModel[], current: InviteModel) => {
        const exists = unique.find(item => item.user.id === current.user.id);
        if (!exists) {
          unique.push(current);
        }
        return unique;
      }, []);
  });

  @Input()
  set value(value: string | string[]) {
    this.value$.set(value);
  }

  get value(): string | string[] {
    return this.value$();
  }

  protected hasSelected(id: string): boolean {
    const value = this.value$();
    if (this.multiple) {
      return Array.isArray(value) && value.includes(id);
    }
    return value === id;
  }

  protected onSelect(item: IdentityModel): void {
    if (!item) {
      return;
    }

    if (this.multiple) {
      const currentValue = Array.isArray(this.value$()) ? [...this.value$()] : [];
      const index = currentValue.indexOf(item.id);

      if (index > -1) {
        currentValue.splice(index, 1);
      } else {
        currentValue.push(item.id);
      }

      this.value$.set(currentValue);
      this.changeItems.emit(currentValue);
    } else {
      this.value$.set(item.id);
      this.changeItems.emit([item.id]);
    }

    this.selectItem.emit(item);
  }

  protected onSelectOwner(): void {
    const currentUser = this.currentUser();
    if (currentUser) {
      this.onSelect(currentUser);
    }
  }

  protected onArrowUp(): void {
    this.selectIndex.update(current => {
      if (current <= 0) {
        return this.filteredItems().length - 1;
      }
      return current - 1;
    });
  }

  protected onArrowDown(): void {
    this.selectIndex.update(current => {
      if (current >= this.filteredItems().length - 1) {
        return 0;
      }
      return current + 1;
    });
  }

  protected onEnterPress(): void {
    const currentIndex = this.selectIndex();
    const items = this.filteredItems();

    if (currentIndex >= 0 && currentIndex < items.length) {
      this.onSelect(items[currentIndex].user);
    }
  }
}
